import React, { useEffect, useRef } from 'react';
import { scrollState, clamp } from '@/lib/depth';

/**
 * The CRT-head character behind the Partnerships section. The video never
 * "plays" — instead its currentTime is scrubbed to the mouse's horizontal
 * position, so the character looks left/right in sync with the cursor (forward
 * and reverse). The source is encoded all-intra (every frame a keyframe) so the
 * seeks are instant and stay glued to the mouse.
 *
 * Visible only while the Partnerships section is in frame (fades with it).
 */
const IN_START = 3900;
const IN_END = 4500;
const OUT_START = 5500;
const OUT_END = 5950;

const INVERT = false; // flip if the head should track the opposite direction
const EDGE = 0.06; // ignore the very first/last slivers of the clip

export const VideoCharacter = () => {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const mouseX = useRef(0.5);
  const cur = useRef(0);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onMove = (e) => {
      mouseX.current = clamp(e.clientX / Math.max(window.innerWidth, 1), 0, 1);
    };
    window.addEventListener('mousemove', onMove, { passive: true });

    // Cloudflare (and any remote origin) serves the MP4 over the network, and
    // Range-request seeking is far slower than from local disk — so when the
    // scrub loop sets currentTime, v.seeking can stay true long enough that
    // every frame is skipped and the head appears frozen. Fix: fetch the whole
    // clip up front, hand the video a blob: URL (fully in memory), and only then
    // enable scrubbing. Local seeks against an in-memory blob are instant.
    const base = process.env.PUBLIC_URL || '';
    let objectUrl = null;
    let ready = false;
    let cancelled = false;
    let raf;

    const onCanPlay = () => {
      ready = true;
    };

    fetch(`${base}/hero3-char.mp4`)
      .then((res) => res.blob())
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        v.addEventListener('loadedmetadata', onCanPlay);
        v.addEventListener('canplay', onCanPlay);
        v.src = objectUrl;
        v.load();
      })
      .catch(() => {});

    let lastSet = -1;
    const tick = () => {
      const cam = scrollState.camera;
      const op = Math.min(
        clamp((cam - IN_START) / (IN_END - IN_START), 0, 1),
        clamp(1 - (cam - OUT_START) / (OUT_END - OUT_START), 0, 1)
      );
      if (wrapRef.current) {
        wrapRef.current.style.opacity = op.toFixed(3);
        wrapRef.current.style.visibility = op < 0.002 ? 'hidden' : 'visible';
      }

      if (ready && v.duration && op > 0.001) {
        const dur = v.duration;
        const mx = INVERT ? 1 - mouseX.current : mouseX.current;
        const target = (EDGE + mx * (1 - 2 * EDGE)) * dur; // map cursor X -> time
        cur.current += (target - cur.current) * 0.2; // ease so it glides in sync
        const t = clamp(cur.current, 0, dur - 0.04);
        if (Math.abs(t - lastSet) > 0.018 && !v.seeking) {
          v.currentTime = t;
          lastSet = t;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      v.removeEventListener('loadedmetadata', onCanPlay);
      v.removeEventListener('canplay', onCanPlay);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="fixed inset-0 z-[4] pointer-events-none"
      style={{ opacity: 0, visibility: 'hidden' }}
      aria-hidden="true"
      data-testid="video-character"
    >
      {/* src is set imperatively to a blob: URL once the clip is fully fetched */}
      <video
        ref={videoRef}
        className="h-full w-full object-cover"
        muted
        playsInline
        preload="auto"
      />
    </div>
  );
};

export default VideoCharacter;
